// state/facade/dataFacade.js
import {createSelector} from '@reduxjs/toolkit';
import {selectDataMode} from '../slice/dataModeSlice';

// RAW
import {fetchSiteDirectionsMetadata as rawFetchSiteMeta, fetchSites as rawFetchSites,} from '../api/siteDataAPI';

// PROCESSED
import {fetchSiteDirectionsMetadata as prFetchSiteMeta, fetchSites as prFetchSites,} from '../api/processedSiteDataAPI';

// MAP FILTERS (raw)
import * as rawMF from '../slice/mapFiltersSlice';
// MAP FILTERS (processed)
import * as prMF from '../slice/processedMapFiltersSlice';

/** Stable empty refs */
const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});

/** Pick the active site slice (stable reference if state didn't change) */
const selectRawSiteSlice = (state) => state?.siteDataAPI ?? EMPTY_OBJECT;
const selectProcessedSiteSlice = (state) => state?.processedSiteDataAPI ?? EMPTY_OBJECT;

const selectActiveSiteSlice = createSelector(
    [selectDataMode, selectRawSiteSlice, selectProcessedSiteSlice],
    (mode, raw, processed) => (mode === 'processed' ? processed : raw)
);

/** SELECTORS (mode-aware, memoized, stable) */
export const selectSitesList = createSelector([selectActiveSiteSlice], (slice) =>
    // prefer `list`, fall back to `data`, otherwise stable EMPTY_ARRAY
    (slice?.list ?? slice?.data) ?? EMPTY_ARRAY
);

export const selectLocationsMap = createSelector([selectActiveSiteSlice], (slice) =>
    slice?.locations ?? EMPTY_OBJECT
);

export const selectSiteMeta = createSelector([selectActiveSiteSlice], (slice) =>
    slice?.metadata ?? EMPTY_ARRAY
);

export const selectSitesLoading = createSelector([selectActiveSiteSlice], (slice) =>
    Boolean(slice?.loading)
);

/** THUNKS (mode-aware) */
export const fetchSitesActive = () => (dispatch, getState) => {
  const mode = selectDataMode(getState());
  return mode === 'processed' ? dispatch(prFetchSites()) : dispatch(rawFetchSites());
};

export const fetchSiteDirectionsMetadataActive = (args) => (dispatch, getState) => {
  const mode = selectDataMode(getState());
  return mode === 'processed'
      ? dispatch(prFetchSiteMeta(args))
      : dispatch(rawFetchSiteMeta(args));
};

/** MAP FILTERS (mode-aware action creators) */
const pickMF = (state) => (selectDataMode(state) === 'processed' ? prMF : rawMF);

export const includeSelectedSiteDirectionsActive = (items) => (dispatch, getState) =>
    dispatch(pickMF(getState()).includeSelectedSiteDirections(items));

export const excludeSelectedSiteDirectionsActive = (items) => (dispatch, getState) =>
    dispatch(pickMF(getState()).excludeSelectedSiteDirections(items));

export const setIncludedSiteDirectionsActive = (items) => (dispatch, getState) =>
    dispatch(pickMF(getState()).setIncludedSiteDirections(items));

export const setExcludedSiteDirectionsActive = (items) => (dispatch, getState) =>
    dispatch(pickMF(getState()).setExcludedSiteDirections(items));

export const toggleExcludedSiteDirectionActive = (name) => (dispatch, getState) =>
    dispatch(pickMF(getState()).toggleExcludedSiteDirection(name));
